import { Outlet, Link, useLocation, useParams, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyPlans } from "@/hooks/useCompanyPlans";
import { useExpenseBadges } from "@/hooks/use-expense-badges";
import { cn } from "@/lib/utils";
import { getSystemIcon, getSystemConfig, getModuleIcon } from "@/lib/system-icons";
import {
  Building2,
  Users,
  Settings,
  BarChart3,
  FileText,
  CreditCard,
  LogOut,
  HelpCircle,
  Layout,
  User,
  Sun,
  Moon,
  Monitor,
  Shield,
  Package,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Car,
  UserCircle,
  Wrench,
  Building,
  DollarSign,
  Cog,
  TrendingUp,
  CircleDashed,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/contexts/theme-provider";
import { useState, useEffect } from "react";
import { CartButton } from "@/components/cart/CartButton";
import { CartDrawer } from "@/components/cart/CartDrawer";

function CompanyAdminSidebar({ 
  user, 
  profile, 
  signOut, 
  setTheme 
}: { 
  user: any; 
  profile: any; 
  signOut: () => void; 
  setTheme: (theme: string) => void; 
}) {
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const { companies } = useAuth();
  
  const company = companies.find(c => c.slug === slug);
  const { data: companyPlans } = useCompanyPlans(company?.id);
  
  const currentPath = location.pathname;
  
  const [expandedSystems, setExpandedSystems] = useState<Record<string, boolean>>({});
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});
  const [isCompanyAdminExpanded, setIsCompanyAdminExpanded] = useState(false);

  const expenseBadge = useExpenseBadges();

  const isActive = (path: string) => {
    const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
    const cleanCurrent = currentPath.endsWith("/") ? currentPath.slice(0, -1) : currentPath;
    
    // Match exato primeiro
    if (cleanCurrent === cleanPath) return true;
    
    // Para módulos/submódulos, só destacar se for filho direto (sem mais "/" após o próximo segmento)
    const regex = new RegExp(`^${cleanPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(/[^/]+)?$`);
    return regex.test(cleanCurrent);
  };

  const isSystemActive = (systemCode: string) => {
    return currentPath.includes(`/admin/systems/${systemCode}`);
  };

  const toggleSystem = (systemCode: string) => {
    setExpandedSystems(prev => ({ ...prev, [systemCode]: !prev[systemCode] }));
  };

  const renderModuleBadge = (moduleCode: string) => {
    if (moduleCode === 'expenses' && expenseBadge) {
      return (
        <Badge 
          variant={expenseBadge.variant} 
          className="ml-auto text-[10px] px-1.5 py-0.5 h-4 min-w-[20px] flex items-center justify-center"
        >
          {expenseBadge.count}
        </Badge>
      );
    }
    return null;
  };


  // Auto-expand do sistema ativo
  useEffect(() => {
    activeSystems.forEach(plan => {
      if (isSystemActive(plan.system_code)) {
        setExpandedSystems(prev => ({ ...prev, [plan.system_code]: true }));
        
        // Auto-expand módulos com submódulos ativos
        const systemConfig = getSystemConfig(plan.system_code);
        systemConfig?.modules.forEach(module => {
          if (module.subModules) {
            const hasActiveSubModule = module.subModules.some(sub => 
              currentPath.includes(sub.path)
            );
            if (hasActiveSubModule) {
              setExpandedModules(prev => ({ 
                ...prev, 
                [`${plan.system_code}-${module.code}`]: true 
              }));
            }
          }
        });
      }
    });
    // Auto-expand company admin se a URL contém /admin/(users|plans|billing|settings|reports)
    if (currentPath.match(/\/admin\/(users|plans|billing|settings|reports)/)) {
      setIsCompanyAdminExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  // Base navigation items para admin
  const baseNavItems = [
    { title: "Dashboard", url: `/company/${slug}/admin`, icon: LayoutDashboard },
  ];

  const companyAdminItems = [
    { title: "Usuários", url: `/company/${slug}/admin/users`, icon: Users },
    { title: "Planos", url: `/company/${slug}/admin/plans`, icon: Package },
    { title: "Faturamento", url: `/company/${slug}/admin/billing`, icon: CreditCard },
    { title: "Configurações", url: `/company/${slug}/admin/settings`, icon: Settings },
    { title: "Relatórios", url: `/company/${slug}/admin/reports`, icon: FileText },
  ];

  // Get active systems for the company
  const activeSystems = companyPlans?.filter(p => 
    p.status === 'active' && 
    p.system_code && 
    p.system_code !== 'unknown' && 
    p.system_id
  ) || [];

  // Sistemas com seus módulos
  const systemsWithModules = activeSystems.map((plan: any) => {
    const systemConfig = getSystemConfig(plan.system_code);
    const baseModules = systemConfig?.modules || [];
    
    return {
      ...plan,
      systemName: systemConfig?.name || plan.system_name, // Usar nome do config para TrackingONE
      icon: getSystemIcon(plan.system_code) ?? Package,
      modules: baseModules,
    };
  });

  return (
    <Sidebar className="border-r bg-background">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="flex items-center gap-2 px-4 py-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary">
              <Building2 className="h-3 w-3 text-primary-foreground" />
            </div>
            {company?.name}
            <Badge variant="outline" className="ml-auto text-xs">
              Admin
            </Badge>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {baseNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              
              {/* Administrar Empresa Dropdown */}
              <Collapsible open={isCompanyAdminExpanded} onOpenChange={setIsCompanyAdminExpanded}>
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className={cn(
                        "w-full justify-between",
                        currentPath.match(/\/admin\/(users|plans|billing|settings|reports)/) && "bg-accent text-accent-foreground"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>Administrar Empresa</span>
                      </div>
                      {isCompanyAdminExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {companyAdminItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <SidebarMenuSubItem key={item.title}>
                            <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                              <Link to={item.url}>
                                <Icon className="h-4 w-4" />
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {systemsWithModules.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Sistemas Liberados</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {systemsWithModules.map((plan: any) => {
                  const SystemIcon = plan.icon || Package;
                  const isExpanded = Boolean(expandedSystems[plan.system_code]);
                  const systemActive = isSystemActive(plan.system_code);

                  const hasModules = Array.isArray(plan.modules) && plan.modules.length > 0;

                  return (
                    <SidebarMenuItem key={plan.system_id}>
                      <SidebarMenuButton
                        className={cn(
                          "w-full justify-between",
                          systemActive && "bg-accent text-accent-foreground"
                        )}
                        onClick={() => toggleSystem(plan.system_code)}
                        aria-expanded={isExpanded}
                        aria-controls={`system-${plan.system_code}-submenu`}
                      >
                        <div className="flex items-center gap-2">
                          <SystemIcon className="h-4 w-4" />
                          <span>{plan.systemName}</span>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </SidebarMenuButton>

                      {isExpanded && (
                        <SidebarMenuSub id={`system-${plan.system_code}-submenu`}>
                          {hasModules ? (
                            plan.modules.map((module: any) => {
                              const moduleUrl = `/company/${slug}/admin/systems/${plan.system_code}${module.path}`;
                              const ModuleIcon = module.icon || getModuleIcon(module.code);
                              const hasSubModules = Array.isArray(module.subModules) && module.subModules.length > 0;
                              const moduleKey = `${plan.system_code}-${module.code}`;
                              const isModuleExpanded = Boolean(expandedModules[moduleKey]);

                              // Se o módulo tem submódulos, renderiza como Collapsible
                              if (hasSubModules) {
                                return (
                                  <Collapsible 
                                    key={moduleKey}
                                    open={isModuleExpanded}
                                    onOpenChange={(open) => setExpandedModules(prev => ({ ...prev, [moduleKey]: open }))}
                                  >
                                    <SidebarMenuSubItem>
                                      <CollapsibleTrigger asChild>
                                        <SidebarMenuSubButton className="w-full justify-between">
                                          <div className="flex items-center gap-2 flex-1">
                                            <ModuleIcon className="h-4 w-4" />
                                            <span className="flex-1">{module.name}</span>
                                            {renderModuleBadge(module.code)}
                                          </div>
                                          {isModuleExpanded ? (
                                            <ChevronDown className="h-3 w-3" />
                                          ) : (
                                            <ChevronRight className="h-3 w-3" />
                                          )}
                                        </SidebarMenuSubButton>
                                      </CollapsibleTrigger>
                                      <CollapsibleContent>
                                        <SidebarMenuSub>
                                          {module.subModules.map((subModule: any) => {
                                            const subModuleUrl = `/company/${slug}/admin/systems/${plan.system_code}${subModule.path}`;
                                            const SubModuleIcon = subModule.icon || getModuleIcon(subModule.code);
                                            return (
                                              <SidebarMenuSubItem key={`${moduleKey}-${subModule.code}`}>
                                                <SidebarMenuSubButton asChild isActive={isActive(subModuleUrl)} className="max-w-full">
                                                  <Link to={subModuleUrl} className="flex items-center gap-2 w-full overflow-hidden">
                                                    <SubModuleIcon className="h-3.5 w-3.5 shrink-0" />
                                                    <span className="truncate">{subModule.name}</span>
                                                  </Link>
                                                </SidebarMenuSubButton>
                                              </SidebarMenuSubItem>
                                            );
                                          })}
                                        </SidebarMenuSub>
                                      </CollapsibleContent>
                                    </SidebarMenuSubItem>
                                  </Collapsible>
                                );
                              }

                              // Módulos sem submódulos renderizam normalmente
                              return (
                                <SidebarMenuSubItem key={moduleKey}>
                                  <SidebarMenuSubButton asChild isActive={isActive(moduleUrl)} className="max-w-full">
                                    <Link to={moduleUrl} className="flex items-center gap-2 w-full overflow-hidden">
                                      <ModuleIcon className="h-4 w-4 shrink-0" />
                                      <span className="flex-1 truncate">{module.name}</span>
                                      {renderModuleBadge(module.code)}
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })
                          ) : (
                            <SidebarMenuSubItem>
                              <span className="text-muted-foreground text-sm">Sem módulos</span>
                            </SidebarMenuSubItem>
                          )}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to={`/company/${slug}/admin/support`}>
                <HelpCircle className="h-4 w-4" />
                <span>Suporte</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          
          {/* Menu do Usuário */}
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="w-full">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback>
                      {profile?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{profile?.full_name || user?.email || 'Usuário'}</span>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/profile">
                    <User className="mr-2 h-4 w-4" />
                    Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTheme("light")}>
                  <Sun className="mr-2 h-4 w-4" />
                  Tema Claro
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>
                  <Moon className="mr-2 h-4 w-4" />
                  Tema Escuro
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>
                  <Monitor className="mr-2 h-4 w-4" />
                  Sistema
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

export default function CompanyAdminLayout() {
  const { slug } = useParams<{ slug: string }>();
  const { companies, hasCompanyRole, user, loading, signOut, profile, setActiveCompany } = useAuth();
  const { setTheme } = useTheme();
  const location = useLocation();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const isLiveMap = location.pathname.includes('/systems/fleet/monitoring/live');
  const isStorePage = location.pathname.includes('/systems/fleet/store');
  
  const company = companies.find(c => c.slug === slug);

  // Sync active company with URL slug
  useEffect(() => {
    console.log('[CompanyAdminLayout] Setting active company:', { 
      companyId: company?.id, 
      companySlug: company?.slug 
    });
    if (company && company.id) {
      setActiveCompany(company);
    }
  }, [company, setActiveCompany]);

  if (loading) {
    return <div className="flex h-screen items-center justify-center">Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!company) {
    return <Navigate to="/" replace />;
  }

  // Check if user has admin role for this company
  if (!hasCompanyRole(company.id, 'company_admin')) {
    return <Navigate to={`/company/${slug}/user`} replace />;
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <CompanyAdminSidebar 
          user={user}
          profile={profile}
          signOut={signOut}
          setTheme={setTheme}
        />
        
        <div className="flex-1 flex flex-col">
          {!isLiveMap && (
            <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex h-14 items-center gap-4 px-6">
                <SidebarTrigger />
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  <h1 className="font-semibold">{company.name}</h1>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {isStorePage && <CartButton onClick={() => setIsCartOpen(true)} />}
                </div>
              </div>
            </header>
          )}

          <main className={isLiveMap ? "p-0" : "p-6"}>
            <Outlet />
          </main>
        </div>
      </div>
      
      <CartDrawer open={isCartOpen} onOpenChange={setIsCartOpen} />
    </SidebarProvider>
  );
}